<script setup>
import { router } from '@inertiajs/vue3';
import InputField from '@js/components/fields/InputField.vue';
import ListboxField from '@js/components/fields/ListboxField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import { useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref } from 'vue';
import { boolean, object, string } from 'yup';

const { tenant, externalService } = defineProps({
    tenant: {
        required: true,
        type: Object,
    },
    externalService: {
        required: true,
        type: Object,
    },
});

const { route } = useRoute();

const regionOptions = [
    { value: 'eu', label: 'Europe' },
    { value: 'na', label: 'Amérique du Nord' },
    { value: 'fe', label: 'Extrême-Orient' },
];

const initialRegionValue = externalService.data?.region ?? 'eu';

const isSubmitting = ref(false);

const form = reactive({
    active: externalService.active ?? false,
    refreshToken: externalService.data?.refreshToken ?? '',
    lwaClientId: externalService.data?.lwaClientId ?? '',
    lwaClientSecret: externalService.data?.lwaClientSecret ?? '',
    region:
        regionOptions.find((option) => option.value === initialRegionValue) ||
        regionOptions[0],
    sellerId: externalService.data?.sellerId ?? '',
    marketplaceIds: (externalService.data?.marketplaceIds ?? []).join(','),
});

const validationSchema = object({
    active: boolean().required('Ce champ est requis.'),
    region: object().required('Ce champ est requis.'),
    sellerId: string().nullable(),
    marketplaceIds: string().nullable(),
    lwaClientId: string().nullable(),
    lwaClientSecret: string().nullable(),
    refreshToken: string().nullable(),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(
            route(
                'front.projectManagement.customer.externalServices.postAmazon',
                {
                    tenant: tenant.id,
                },
            ),
            {
                active: form.active,
                refreshToken: form.refreshToken,
                lwaClientId: form.lwaClientId,
                lwaClientSecret: form.lwaClientSecret,
                region: form.region.value,
                sellerId: form.sellerId,
                marketplaceIds: form.marketplaceIds
                    .split(',')
                    .map((id) => id.trim())
                    .filter(Boolean),
            },
        )
        .then(() => {
            toastSaved();

            router.visit(
                route(
                    'front.projectManagement.customer.externalServices.amazon',
                    {
                        tenant: tenant.id,
                    },
                ),
            );
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(
                route('front.projectManagement.customer.externalServices'),
            );
        });
}
</script>

<template>
    <FormRoot
        v-slot="{ errors }"
        :validation-schema="validationSchema"
        :submit="submit"
    >
        <FormColumn>
            <FormContent>
                <SwitchField
                    v-model="form.active"
                    label="Actif"
                    field-name="active"
                    field-id="active"
                />
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <ListboxField
                        v-model="form.region"
                        :options="regionOptions"
                        label="Région SP-API"
                        field-name="region"
                        field-id="region"
                        required
                    >
                        <template #buttonContent>
                            {{ form.region.label }}
                        </template>

                        <template #optionContent="{ option }">
                            {{ option.label }}
                        </template>
                    </ListboxField>

                    <InputField
                        v-model="form.sellerId"
                        label="Identifiant vendeur"
                        field-name="sellerId"
                        field-id="sellerId"
                    />

                    <InputField
                        v-model="form.marketplaceIds"
                        label="Identifiants de marketplace (séparés par virgule)"
                        field-name="marketplaceIds"
                        field-id="marketplaceIds"
                    />
                </FormGrid>
            </FormContent>

            <FormContent>
                <FormGrid :columns="2">
                    <InputField
                        v-model="form.lwaClientId"
                        label="Identifiant client LWA"
                        field-name="lwaClientId"
                        field-id="lwaClientId"
                    />

                    <InputField
                        v-model="form.lwaClientSecret"
                        label="Clé secrète client LWA"
                        field-name="lwaClientSecret"
                        field-id="lwaClientSecret"
                    />

                    <InputField
                        v-model="form.refreshToken"
                        label="Jeton de rafraîchissement"
                        field-name="refreshToken"
                        field-id="refreshToken"
                    />
                </FormGrid>
            </FormContent>
        </FormColumn>

        <FormFooter :is-submitting="isSubmitting" :errors="errors" />
    </FormRoot>
</template>
